import { Injectable } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { LookupItem, RejectReason, Settlement, ComplaintReason } from '../models/lookup.model';
import { environment } from '../../../environments/environment';
import { CACHE_ENABLED } from '../interceptors/caching.interceptor';

@Injectable({
  providedIn: 'root'
})
export class LookupService {
  private readonly baseUrl = `${environment.apiUrl}/Lookups`;

  constructor(private http: HttpClient) {}

  private getData<T>(endpoint: string): Observable<T> {
    const url = `${this.baseUrl}/${endpoint}`;

    return this.http.get<{ success: boolean; data: T; error: any }>(url, {
      context: new HttpContext().set(CACHE_ENABLED, true)
    }).pipe(
      map(res => res.data)
    );
  }

  getAdminRoles(): Observable<LookupItem[]> {
    return this.getData<LookupItem[]>('admin-roles');
  }

  getUserTypes(): Observable<LookupItem[]> {
    return this.getData<LookupItem[]>('user-types');
  }

  getDealTypes(): Observable<LookupItem[]> {
    return this.getData<LookupItem[]>('deal-types');
  }

  getPropertyTypes(): Observable<LookupItem[]> {
    return this.getData<LookupItem[]>('property-types');
  }

  getRenovationStatuses(): Observable<LookupItem[]> {
    return this.getData<LookupItem[]>('renovation-statuses');
  }

  getRentDurations(): Observable<LookupItem[]> {
    return this.getData<LookupItem[]>('rent-durations');
  }

  getRoomCounts(): Observable<LookupItem[]> {
    return this.getData<LookupItem[]>('room-counts');
  }

  getAdministrativeAreas(): Observable<LookupItem[]> {
    return this.getData<LookupItem[]>('administrative-areas');
  }

  getAbsheronAreas(): Observable<LookupItem[]> {
    return this.getData<LookupItem[]>('absheron-areas');
  }

  getBakuDistricts(): Observable<LookupItem[]> {
    return this.getData<LookupItem[]>('baku-districts');
  }

  getSettlements(districtId: number | string): Observable<Settlement[]> {
    return this.getData<Settlement[]>(`baku-districts/${districtId}/settlements`);
  }

  getBakuMetroStations(): Observable<LookupItem[]> {
    return this.getData<LookupItem[]>('baku-metro-stations').pipe(
      map(stations => stations.map(station => {
        const coords = BAKU_METRO_COORDS[station.name.trim()];
        if (coords) {
          return { ...station, ...coords };
        }
        return station;
      }))
    );
  }

  getRejectReasons(): Observable<RejectReason[]> {
    const url = `${environment.apiUrl}/RejectReason`;
    return this.http.get<{ success: boolean; data: RejectReason[]; error: any }>(url, {
      context: new HttpContext().set(CACHE_ENABLED, true)
    }).pipe(
      map(res => res.data)
    );
  }

  getComplaintReasons(): Observable<ComplaintReason[]> {
    const url = `${environment.apiUrl}/ComplaintReason/public`;
    return this.http.get<{ success: boolean; data: ComplaintReason[]; error: any }>(url, {
      context: new HttpContext().set(CACHE_ENABLED, true)
    }).pipe(
      map(res => res.data)
    );
  }
}

const BAKU_METRO_COORDS: { [key: string]: { lat: number, lng: number } } = {
  'İçərişəhər': { lat: 40.3661, lng: 49.8311 },
  'Sahil': { lat: 40.3708, lng: 49.8464 },
  '28 May': { lat: 40.3798, lng: 49.8486 },
  'Cəfər Cabbarlı': { lat: 40.3798, lng: 49.8486 },
  'Gənclik': { lat: 40.4001, lng: 49.8512 },
  'Nəriman Nərimanov': { lat: 40.4158, lng: 49.8672 },
  'Ulduz': { lat: 40.4431, lng: 49.8911 },
  'Koroğlu': { lat: 40.4203, lng: 49.9171 },
  'Qara Qarayev': { lat: 40.4174, lng: 49.9351 },
  'Neftçilər': { lat: 40.4116, lng: 49.9427 },
  'Xalqlar Dostluğu': { lat: 40.3976, lng: 49.9531 },
  'Əhmədli': { lat: 40.3857, lng: 49.9542 },
  'Həzi Aslanov': { lat: 40.3734, lng: 49.9535 },
  'Nizami': { lat: 40.3787, lng: 49.8301 },
  'Elmlər Akademiyası': { lat: 40.3752, lng: 49.8152 },
  'İnşaatçılar': { lat: 40.3897, lng: 49.8032 },
  '20 Yanvar': { lat: 40.3958, lng: 49.8144 },
  'Memar Əcəmi': { lat: 40.4111, lng: 49.8151 },
  'Nəsimi': { lat: 40.4261, lng: 49.8144 },
  'Azadlıq prospekti': { lat: 40.4265, lng: 49.8428 },
  'Dərnəgül': { lat: 40.4258, lng: 49.8617 },
  'Bakmıl': { lat: 40.4294, lng: 49.8864 },
  'Avtovağzal': { lat: 40.4201, lng: 49.7919 },
  '8 Noyabr': { lat: 40.3975, lng: 49.8300 },
  'Xocəsən': { lat: 40.4131, lng: 49.7758 }
};
